/**
 * Organization Dashboard Widget System
 *
 * A dynamic, resizable widget grid for the organization dashboard.
 * Each widget is defined by its type and layout (grid position + size).
 */

/**
 * Available widget types for the organization dashboard.
 */
export type OrgWidgetType =
    | 'org-overview'
    | 'org-hierarchy'
    | 'recent-activity'
    | 'security-coverage'
    | 'policy-status'
    | 'policy-acknowledgment'
    | 'user-directory'
    | 'feature-map';

/**
 * Widget layout definition — position and size within the CSS grid.
 * Units are in grid cells (e.g., 1 cell = minmax(280px, 1fr) wide).
 */
export interface OrgWidgetLayout {
    /** Column span (number of grid columns this widget occupies) */
    colSpan: number;
    /** Row span (number of grid rows this widget occupies) */
    rowSpan: number;
    /** Display order (lower = first) */
    order: number;
}

/**
 * Minimum and maximum size constraints for a widget.
 */
export interface OrgWidgetSizeConstraints {
    minColSpan: number;
    maxColSpan: number;
    minRowSpan: number;
    maxRowSpan: number;
}

/**
 * Persisted widget definition (saved to Firestore).
 */
export interface OrgDashboardWidget {
    /** Unique ID */
    id: string;
    /** Widget type */
    type: OrgWidgetType;
    /** Display title */
    title: string;
    /** Grid layout */
    layout: OrgWidgetLayout;
}

/**
 * Runtime widget state (in-memory only, not persisted).
 */
export interface OrgDashboardWidgetState extends OrgDashboardWidget {
    /** Whether this widget is currently being resized */
    isResizing: boolean;
    /** Size constraints for this widget type */
    constraints: OrgWidgetSizeConstraints;
}

/**
 * Data passed to individual widget components.
 */
/** Generic keyed entity for widget data */
export interface OrgWidgetEntity {
    key: string;
    data: Record<string, unknown>;
}

export interface OrgWidgetData {
    /** Organization parts (positions) */
    positions: Record<string, unknown>[];
    /** Hat keys + hat objects */
    hats: OrgWidgetEntity[];
    /** User keys + user objects */
    users: OrgWidgetEntity[];
    /** Security group keys + objects */
    securityGroups: OrgWidgetEntity[];
    /** Policy keys + objects */
    policies: OrgWidgetEntity[];
    /** Feature keys + objects */
    features: OrgWidgetEntity[];
    /** Organization name */
    orgName: string;
    /** Organization key */
    orgKey: string;
    /** User IDs currently online (from presence system) */
    onlineUserIds?: string[];
    /** Individual policy acknowledgment records (from subcollection) */
    policyAcknowledgments?: OrgWidgetEntity[];
    /** Route documents from the routes collection */
    routes?: OrgWidgetEntity[];
    /** Menu items mapped by route path for Feature Map widget */
    menuItems?: OrgWidgetMenuItem[];
    /**
     * Who is looking (ISS-022). When present, widgets show staff only what is theirs; absent means
     * unscoped (everything), so other consumers of the widgets are unaffected.
     */
    viewer?: OrgWidgetViewer;
}

/** The person looking at the organisation dashboard, and what they may see. */
export interface OrgWidgetViewer {
    uid: string;
    /** Org owner, holder of the org-admin permission, or a developer: sees the whole organisation. */
    isOrgAdmin: boolean;
    /** The viewer plus everyone holding a post below a post the viewer holds. */
    teamUids: string[];
}

/** Who last changed an org entity (hat, security group, policy, feature), stamped by the editors. */
export interface OrgLastChangedBy {
    uid: string;
    name: string;
}

/** Simplified menu item info for the Feature Map widget */
export interface OrgWidgetMenuItem {
    /** The route path this menu item links to (e.g. '/app/organization/dashboard') */
    routePath: string;
    /** Display name in the sidebar (e.g. 'Dashboard', 'Org Board') */
    name: string;
    /** Material icon name */
    icon?: string;
    /** SVG icon name */
    svg?: string;
    /** Section like 'organization', 'management-tools', 'workspace' */
    section: string;
    /** Menu tier: 'top' or 'middle' */
    tier: 'top' | 'middle';
}

/**
 * Default widget configurations (factory presets).
 */
export const DEFAULT_ORG_WIDGETS: OrgDashboardWidget[] = [
    {
        id: 'org-overview-default',
        type: 'org-overview',
        title: 'Organization Overview',
        layout: { colSpan: 4, rowSpan: 1, order: 0 },
    },
    {
        id: 'org-hierarchy-default',
        type: 'org-hierarchy',
        title: 'Organization Structure',
        layout: { colSpan: 2, rowSpan: 2, order: 1 },
    },
    {
        id: 'security-coverage-default',
        type: 'security-coverage',
        title: 'Security Coverage',
        layout: { colSpan: 2, rowSpan: 2, order: 2 },
    },
    {
        id: 'recent-activity-default',
        type: 'recent-activity',
        title: 'Recent Activity',
        layout: { colSpan: 2, rowSpan: 2, order: 3 },
    },
    {
        id: 'policy-status-default',
        type: 'policy-status',
        title: 'Policy Status',
        layout: { colSpan: 2, rowSpan: 2, order: 4 },
    },
    {
        id: 'user-directory-default',
        type: 'user-directory',
        title: 'Staff Directory',
        layout: { colSpan: 2, rowSpan: 2, order: 5 },
    },
    {
        id: 'policy-acknowledgment-default',
        type: 'policy-acknowledgment',
        title: 'Policy Acknowledgment',
        layout: { colSpan: 2, rowSpan: 2, order: 6 },
    },
    {
        id: 'feature-map-default',
        type: 'feature-map',
        title: 'Feature & Permission Map',
        layout: { colSpan: 2, rowSpan: 2, order: 7 },
    },
];

/**
 * Size constraints per widget type.
 */
export const WIDGET_CONSTRAINTS: Record<OrgWidgetType, OrgWidgetSizeConstraints> = {
    'org-overview': { minColSpan: 2, maxColSpan: 4, minRowSpan: 1, maxRowSpan: 2 },
    'org-hierarchy': { minColSpan: 1, maxColSpan: 4, minRowSpan: 1, maxRowSpan: 4 },
    'recent-activity': { minColSpan: 1, maxColSpan: 4, minRowSpan: 1, maxRowSpan: 4 },
    'security-coverage': { minColSpan: 1, maxColSpan: 4, minRowSpan: 1, maxRowSpan: 3 },
    'policy-status': { minColSpan: 1, maxColSpan: 4, minRowSpan: 1, maxRowSpan: 3 },
    'user-directory': { minColSpan: 1, maxColSpan: 4, minRowSpan: 1, maxRowSpan: 4 },
    'policy-acknowledgment': { minColSpan: 1, maxColSpan: 4, minRowSpan: 1, maxRowSpan: 4 },
    'feature-map': { minColSpan: 1, maxColSpan: 4, minRowSpan: 1, maxRowSpan: 4 },
};
